import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Loader2, Sprout } from "lucide-react";

const SubstituteDialog = ({ open, onOpenChange, recipeId, ingredient }) => {
  const [haveInstead, setHaveInstead] = useState("");
  const [busy, setBusy] = useState(false);
  const [suggestion, setSuggestion] = useState(null);

  // Reset on close
  const handleOpenChange = (v) => {
    if (!v) {
      setHaveInstead("");
      setSuggestion(null);
      setBusy(false);
    }
    onOpenChange(v);
  };

  const ask = async (e) => {
    e.preventDefault();
    setBusy(true);
    setSuggestion(null);
    try {
      const res = await api.post(`/recipes/${recipeId}/substitute`, {
        ingredient: ingredient?.name || "",
        have_instead: haveInstead.trim() || null,
      });
      setSuggestion(res.data.suggestion);
    } catch {
      toast.error("Couldn't fetch a suggestion. Try again in a sec.");
    } finally {
      setBusy(false);
    }
  };

  if (!ingredient) return null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid="substitute-dialog"
        className="bg-stone-950 border border-stone-800 rounded-none max-w-lg p-0"
      >
        <div className="p-8">
          <DialogHeader className="mb-6">
            <div className="eyebrow mb-3 flex items-center gap-2">
              <Sprout className="w-3.5 h-3.5 text-lime-500" />
              Kitchen swap
            </div>
            <DialogTitle className="font-serif-display text-3xl md:text-4xl text-stone-100 tracking-tight leading-tight">
              Out of <span className="italic text-orange-600">{ingredient.name}</span>?
            </DialogTitle>
            <p className="text-sm text-stone-400 mt-3">
              Tell us what you&apos;ve got instead, or leave it blank and the kitchen will pick something clever.
            </p>
          </DialogHeader>

          <form onSubmit={ask} className="space-y-5">
            <input
              data-testid="substitute-have-instead"
              value={haveInstead}
              onChange={(e) => setHaveInstead(e.target.value)}
              placeholder="e.g. cod, oat milk, dried thyme..."
              className="w-full bg-stone-900 border border-stone-800 focus:border-orange-600 outline-none px-4 py-3 text-stone-100 placeholder-stone-600 transition-colors"
              maxLength={100}
            />
            <button
              type="submit"
              disabled={busy}
              data-testid="substitute-ask"
              className="w-full py-3.5 bg-orange-700 hover:bg-orange-800 disabled:opacity-70 text-stone-50 text-xs uppercase tracking-[0.24em] font-medium transition-colors flex items-center justify-center gap-2"
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              {busy ? "Thinking..." : "Ask the kitchen"}
            </button>
          </form>

          {suggestion && (
            <div
              data-testid="substitute-suggestion"
              className="mt-6 border border-lime-800/60 bg-lime-950/20 p-5"
            >
              <div className="eyebrow mb-3 text-lime-500">The kitchen says</div>
              <p className="font-serif-display italic text-lg text-stone-100 leading-relaxed">
                {`\u201C${suggestion}\u201D`}
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SubstituteDialog;
